import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { RunEstimate } from '../api/types';
import { Portal } from './Portal';

/** Header-level "run the whole store now" — one on-demand execution of the
 * rendering job, behind the same cost-estimate confirm the Queued panel uses.
 * Lives in the QA header (replaced the vestigial scanner "N pending" chip);
 * per-product re-runs stay on the Diagnose card. */
export function StoreRunButton({ storeId }: { storeId: string }) {
  const [open, setOpen] = useState(false);
  const [estimate, setEstimate] = useState<RunEstimate | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const runM = useMutation({
    mutationFn: () => api.reprocessQueueRun(storeId),
    onSuccess: (res) => {
      setResult(`Render started (${res.execution.split('/').pop()}). Changed products re-render in this run.`);
    },
    onError: (e) => {
      setResult(`Couldn't start the run: ${e instanceof HttpError ? e.detail : (e as Error).message}`);
    },
  });

  async function openConfirm() {
    setResult(null);
    try {
      setEstimate(await api.runEstimate(storeId));
    } catch {
      setEstimate(null); // estimate is informational; the confirm still gates
    }
    setOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={openConfirm}
        className="ff-chip cursor-pointer border border-ff-ink bg-ff-ink text-white transition-colors hover:bg-ff-ink-soft"
        data-testid="store-run-open"
      >
        Run store now…
      </button>
      {open && (
        <Portal>
          {/* Portal escapes the header's backdrop-blur stacking context. */}
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-ff-ink/70 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => !runM.isPending && setOpen(false)}
          >
            <div
              className="w-[min(92vw,32rem)] space-y-3 rounded-2xl border border-white/40 bg-white p-6 shadow-hover"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-label="Confirm paid render run"
            >
              <div className="font-display text-lg font-bold text-ff-ink">
                Run the whole store now?
              </div>
              <p className="text-sm text-ff-ink/90">
                This executes the <strong>whole rendering job</strong> for{' '}
                <strong>{storeId}</strong>
                {estimate && (
                  <>
                    {' '}— up to <strong>{estimate.n_products.toLocaleString()}</strong>{' '}
                    products ≈ <strong>${estimate.max_cost_usd.toFixed(2)}</strong> worst-case
                  </>
                )}
                . Steady-state runs only render changed products (cache hits are free).
              </p>
              {result && <p className="text-sm text-ff-forest">{result}</p>}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  className="ff-btn-ghost"
                  onClick={() => setOpen(false)}
                  disabled={runM.isPending}
                >
                  {result ? 'Close' : 'Cancel'}
                </button>
                {!result && (
                  <button
                    type="button"
                    className="ff-btn-danger px-4 py-2"
                    onClick={() => runM.mutate()}
                    disabled={runM.isPending}
                    data-testid="store-run-confirm"
                  >
                    {runM.isPending ? 'Starting…' : 'Run the job now'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
